import { call, put, select, takeLatest, all } from 'redux-saga/effects';

import API from '../../api';
// import { messages } from '../i18n';
import i18n from '../../i18n';
import storageService from '../../services/StorageService';
import { registerLoginEvent } from '../../services/reactGa';
import { AppState, VerificationStep } from '../interfaces';
import { sendLog, setIsReviewApp, setWizardStep } from '../app/acions';
import { isAndroid } from '../../helpers/os';
import { nativeMessageService } from '../../services/NativeMessageService';
import { generateAndroidIdentifiers } from '../../api/helpers';
import { isDebugEnabled } from '../../common/isDebugEnables';

import { actionCreators } from './actions';

const makeResponseFulfill = (response) => ({
  data: response?.data,
  status: response?.status,
});

function* handleVerifyFulfill(action, stepType: VerificationStep, failureAction, errorMessage) {
  const { response } = action.payload;
  const { data, status } = response;
  const { step, license_number, phone_number, verification_code, email } = yield select(
    (app: AppState) => app.userForms.verification
  );

  if (stepType === VerificationStep.email) {
    yield put(
      sendLog({
        tag: `App:${step}:Fulfill`,
        lyftResponse: response,
        state: {
          license_number,
          phone_number,
          verification_code,
          email,
        },
      })
    );
  }

  if (data?.access_token) {
    yield put(actionCreators.login.request(response));
    return;
  }

  if (step !== stepType && !isDebugEnabled()) {
    return;
  }

  const identifier = data?.challenges?.[0]?.identifier;
  if (
    identifier &&
    identifier !== stepType &&
    [VerificationStep.licenseNumber, VerificationStep.email].includes(identifier)
  ) {
    yield put(actionCreators.setStep(identifier));
    return;
  }

  yield put(
    sendLog({
      tag: `App:${step}:Error`,
      lyftResponse: response,
      state: {
        license_number,
        phone_number,
        verification_code,
        email,
      },
    })
  );

  yield put(
    failureAction({
      code: data?.error,
      description: data?.error_description || errorMessage,
      status,
    })
  );
}

function* handleGetLyftHeaders() {
  try {
    const response = yield call(API.authV2.getParams);
    const {
      access_token_license: accessTokenLicense,
      access_token_params: accessTokenParams,
      access_token_phone: accessTokenPhone,
      account_identifiers: accountIdentifiers,
      tcs_config: tcsConfig,
      phone_auth: phoneAuth,
    } = response;

    yield put(
      actionCreators.getLyftHeaders.success({
        accessTokenLicense,
        accessTokenParams,
        accessTokenPhone,
        accountIdentifiers,
        phoneAuth,
        tcsConfig,
      })
    );
    yield put(actionCreators.getLyftAuthAccessToken.request());
  } catch (e) {
    yield put(actionCreators.getLyftAuthAccessToken.failure());
  }
}

function* handleGetLyftAuthAccessToken() {
  const { authHeaders } = yield select((app: AppState) => app.userForms.verification);
  try {
    const response = yield call(API.lyftApi.access_token, {
      headers: authHeaders.accessTokenParams,
      repeatCount: 1,
      type: 'getLyftAuthAccessToken',
    });

    if (isAndroid) {
      return;
    }

    yield put(actionCreators.getLyftAuthAccessToken.fulfill(makeResponseFulfill(response)));
  } catch (e) {
    yield put(actionCreators.getLyftAuthAccessToken.fulfill(makeResponseFulfill(e.response)));
  }
}

function* handleGetLyftAuthAccessTokenFulfill(action) {
  const { response } = action.payload;

  const phoneNumber = yield select((app: AppState) => app.userForms.verification.phone_number);

  if (response.status < 300) {
    const { token_type, access_token } = response.data;
    yield put(actionCreators.getLyftAuthAccessToken.success({ token_type, access_token }));
    yield put(actionCreators.sendTcsConfig.request());
    yield put(actionCreators.verifyPhoneNumber.request(phoneNumber));
    return;
  }

  yield put(actionCreators.getLyftAuthAccessToken.failure());
}

function* handleSendTcsConfig() {
  const { token_type, access_token, authHeaders } = yield select(
    (app: AppState) => app.userForms.verification
  );

  try {
    yield call(API.lyftApi.tcsConfig, {
      headers: authHeaders.tcsConfig,
      token_type,
      access_token,
      params: {
        accessibility_enabled: false,
        client_id: storageService.getDeviceId(),
        // TODO: wtf???
        // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
        variable_configurations_etag: -7927808274011781469,
      },
      type: 'sendTcsConfig',
    });
  } catch (e) {
    console.log(e);
  }
}

function* verifyPhoneNumber({ payload }) {
  const { value } = payload;
  const { token_type, access_token, authHeaders } = yield select(
    (app: AppState) => app.userForms.verification
  );

  if (!access_token && !token_type) {
    yield put(actionCreators.getLyftHeaders.request());
    return;
  }

  try {
    const response = yield call(API.lyftApi.phoneAuth, {
      token_type,
      access_token,
      headers: authHeaders.phoneAuth,
      phone: value,
      type: 'verifyPhoneNumber',
    });

    if (isAndroid) {
      return;
    }

    yield put(actionCreators.verifyPhoneNumber.fulfill(makeResponseFulfill(response)));
  } catch (e) {
    console.log(e);
    yield put(actionCreators.verifyPhoneNumber.fulfill(makeResponseFulfill(e.response)));
  }
}

function* verifyPhoneNumberFulfill(action) {
  const { response } = action.payload;
  const { data, status } = response;

  const { step } = yield select((app: AppState) => app.userForms.verification);

  if (step !== VerificationStep.phoneNumber) {
    return;
  }

  if (status < 300) {
    yield put(actionCreators.setStep(VerificationStep.verificationCode));
    return;
  }

  yield put(
    actionCreators.verifyPhoneNumber.failure({
      code: data?.error,
      description: data?.error_description || i18n.t('verification.phoneNumber.error'),
      status,
    })
  );
}

function* verifyVerificationCode({ payload }) {
  const { value } = payload;

  const { phone_number, authHeaders } = yield select((app: AppState) => app.userForms.verification);

  try {
    const response = yield call(API.lyftApi.token, {
      phone: phone_number,
      code: value,
      headers: authHeaders.accessTokenPhone,
      type: 'verifyVerificationCode',
    });

    if (isAndroid) {
      return;
    }

    yield put(actionCreators.verifyVerificationCode.fulfill(makeResponseFulfill(response)));
  } catch (e) {
    yield put(actionCreators.verifyVerificationCode.fulfill(makeResponseFulfill(e.response)));
  }
}

function* verifyVerificationCodeFulfill(action) {
  yield call(
    handleVerifyFulfill,
    action,
    VerificationStep.verificationCode,
    actionCreators.verifyVerificationCode.failure,
    i18n.t('verification.phoneNumber.error')
  );
}

function* verifyLicenceNumber({ payload }) {
  const { value } = payload;

  const { phone_number, authHeaders, verification_code } = yield select(
    (app: AppState) => app.userForms.verification
  );

  try {
    const response = yield call(API.lyftApi.token, {
      phone: phone_number,
      code: verification_code,
      headers: authHeaders.accessTokenLicense,
      drivers_license_number: value,
      repeatCount: 1,
      type: 'verifyLicenceNumber',
    });

    if (isAndroid) {
      return;
    }

    yield put(actionCreators.verifyLicenceNumber.fulfill(makeResponseFulfill(response)));
  } catch (e) {
    yield put(actionCreators.verifyLicenceNumber.fulfill(makeResponseFulfill(e.response)));
  }
}

function* verifyLicenceNumberFulfill(action) {
  yield call(
    handleVerifyFulfill,
    action,
    VerificationStep.licenseNumber,
    actionCreators.verifyLicenceNumber.failure,
    'The DL number you entered does not match Lyft records. Please, enter the correct DL number.'
  );
}

function* verifyEmail({ payload }) {
  const { value } = payload;

  const { phone_number, authHeaders, verification_code } = yield select(
    (app: AppState) => app.userForms.verification
  );

  try {
    const response = yield call(API.lyftApi.token, {
      phone: phone_number,
      code: verification_code,
      headers: authHeaders.accessTokenLicense,
      email: value,
      repeatCount: 1,
      type: 'verifyEmail',
    });

    if (isAndroid) {
      return;
    }

    yield put(actionCreators.verifyEmail.fulfill(makeResponseFulfill(response)));
  } catch (e) {
    yield put(actionCreators.verifyEmail.fulfill(makeResponseFulfill(e.response)));
  }
}

function* verifyEmailFulfill(action) {
  yield call(
    handleVerifyFulfill,
    action,
    VerificationStep.email,
    actionCreators.verifyEmail.failure,
    i18n.t('verification.email.error')
  );
}

function* handleLogin(action) {
  const { response: lyftResponse } = action.payload;
  const { data } = lyftResponse;

  const { authHeaders, step, license_number, phone_number, verification_code, email } =
    yield select((app: AppState) => app.userForms.verification);

  const { extension_code, expires_in, access_token, token_type, user_id, refresh_token } = data;

  const response = yield call(API.authV2.auth, {
    phone_number,
    drivers_license_number: license_number,
    token_type,
    access_token,
    expires_in,
    refresh_token,
    user_id,
    extension_code,
  });

  if (response.code > 300) {
    yield put(actionCreators.login.failure());
    yield put(
      sendLog({
        tag: 'App:verifyLogin:Error',
        lyftResponse,
        skeddyResponse: response,
        state: {
          step,
          license_number,
          phone_number,
          verification_code,
          email,
        },
      })
    );
    return;
  }

  const { auth_key, user_photo, user_id: skeddyUserId, is_new_user } = response;

  // singularClient.login(skeddyUserId);

  registerLoginEvent();

  yield put(setIsReviewApp(skeddyUserId === 1548));

  storageService.setRegistrationData({
    phone_number,
    license_number,
  });

  yield put(actionCreators.login.success());
  yield put(actionCreators.setUser({ user_id: skeddyUserId, auth_key, user_photo }));

  if (isAndroid) {
    const accountIdentifierToken = storageService.getAccountIdentifierToken();

    if (accountIdentifierToken) {
      const identifiersHeaders = {
        ...authHeaders.accountIdentifiers,
      };
      identifiersHeaders.authorization = `${token_type} ${access_token}`;

      nativeMessageService.sendNetworkRequestMessage({
        url: `${storageService.getLyftApiUrl()}/v1/accountidentifiers`,
        headers: identifiersHeaders,
        params: {
          identifiers: generateAndroidIdentifiers(accountIdentifierToken),
        },
        method: 'post',
        type: 'accountidentifiers',
      });
    }
  }

  if (is_new_user) {
    yield put(setWizardStep('intro'));
  }
}

function* handleLogout() {
  // singularClient.logout();
  const isReviewApp = yield select((appState: AppState) => appState.app.isReviewApp);

  if (isAndroid && !isReviewApp) {
    const response = yield call(API.authV2.getParams);

    if (response.code === 200) {
      const { revoke_token, lyft_access_token } = response;

      const formData = new FormData();
      formData.append('token', lyft_access_token);

      nativeMessageService.sendNetworkRequestMessage({
        url: `${storageService.getLyftApiUrl()}/oauth2/revoke_token`,
        headers: revoke_token,
        params: Object.fromEntries(formData.entries()),
        method: 'post',
        type: 'revokeToken',
        sendImmediate: true,
      });
    }
  }

  yield call(storageService.clear);
}

const watchers: [any, any][] = [
  [actionCreators.login.REQUEST, handleLogin],
  [actionCreators.logout.type, handleLogout],
  [actionCreators.getLyftHeaders.REQUEST, handleGetLyftHeaders],
  [actionCreators.getLyftAuthAccessToken.REQUEST, handleGetLyftAuthAccessToken],
  [actionCreators.getLyftAuthAccessToken.FULFILL, handleGetLyftAuthAccessTokenFulfill],
  [actionCreators.sendTcsConfig.REQUEST, handleSendTcsConfig],
  [actionCreators.verifyPhoneNumber.REQUEST, verifyPhoneNumber],
  [actionCreators.verifyPhoneNumber.FULFILL, verifyPhoneNumberFulfill],
  [actionCreators.verifyVerificationCode.REQUEST, verifyVerificationCode],
  [actionCreators.verifyVerificationCode.FULFILL, verifyVerificationCodeFulfill],
  [actionCreators.verifyLicenceNumber.REQUEST, verifyLicenceNumber],
  [actionCreators.verifyLicenceNumber.FULFILL, verifyLicenceNumberFulfill],
  [actionCreators.verifyEmail.REQUEST, verifyEmail],
  [actionCreators.verifyEmail.FULFILL, verifyEmailFulfill],
];

export default function* flow() {
  yield all(watchers.map(([actionType, saga]) => takeLatest(actionType, saga)));
}
